import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import { LORAX } from 'HubStyleTokens/colors';
import UICardSection from 'UIComponents/card/UICardSection';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import H4 from 'UIComponents/elements/headings/H4';
import styled from 'styled-components';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import { useSelector } from 'react-redux';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import SuiteTermSelectorContainer from './SuiteTermSelectorContainer';
import SuiteFeatureTableBadge from '../common/SuiteFeatureTableBadge'; // @ts-expect-error svg import

import BLOB from 'bender-url!pricing-pages-ui/img/splash-page/suite-card-top-right-blob.svg';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { CrmSuitePrice } from './price-display/CrmSuitePrice';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { useQueryParamTerm } from '../../utils/urls/queryParamUtils';
import { SUITE_CARD } from '../../constants/tracking/Locations';
import SuiteTileCTAsContainer from './SuiteTileCTAsContainer';
import { isStarterGWP2023EligibleSelector } from '../../selectors/PromotionSelectors';
import Small from 'UIComponents/elements/Small';
import { SUITE_INTERSTITIAL } from '../../constants/pricing/DiscountedKeys';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
export const StyledCardWrapper = styled(UICardWrapper).withConfig({
  displayName: "Ebx632SuiteStarterCard__StyledCardWrapper",
  componentId: "sc-13jdik3-0"
})(["padding:48px 24px 32px 24px;margin-bottom:16px;"]);
export const StyledBlob = styled(UIImage).withConfig({
  displayName: "Ebx632SuiteStarterCard__StyledBlob",
  componentId: "sc-13jdik3-1"
})(["position:absolute;top:0;right:0;width:180px;"]);
const UpgradeHeading = styled(H4).withConfig({
  displayName: "Ebx632SuiteStarterCard__UpgradeHeading",
  componentId: "sc-13jdik3-2"
})(["z-index:1;position:relative;"]);
const Container = styled.div.withConfig({
  displayName: "Ebx632SuiteStarterCard__Container",
  componentId: "sc-13jdik3-3"
})(["max-width:402px;"]);

const SuiteStarterCard = () => {
  const productLevel = STARTER;
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlanLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const isStarterGWP = useSelector(isStarterGWP2023EligibleSelector);
  return /*#__PURE__*/_jsxs(Container, {
    children: [/*#__PURE__*/_jsxs(StyledCardWrapper, {
      children: [/*#__PURE__*/_jsx(UIMediaObject, {
        spacing: "extra-small",
        itemLeft: /*#__PURE__*/_jsx(UIIcon, {
          name: "upgrade",
          size: 24,
          color: LORAX
        }),
        children: /*#__PURE__*/_jsx(UpgradeHeading, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "merchCards.suiteInterstitialCard.upgrade"
          })
        })
      }), /*#__PURE__*/_jsx(StyledBlob, {
        src: BLOB
      }), /*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
        badgeStyle: {
          top: '12px',
          right: '12px'
        }
      }), /*#__PURE__*/_jsxs(UICardSection, {
        className: "p-all-0",
        children: [/*#__PURE__*/_jsx(UIList, {
          childClassName: "m-bottom-4 align-end",
          lastChildClassName: "m-bottom-6",
          children: ['valuePropOne', 'valuePropTwo', 'valuePropThree'].map((translationKey, index) => /*#__PURE__*/_jsxs(UIFlex, {
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: "success",
              size: "xxs",
              className: "m-right-3"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              className: "align-end",
              message: `merchCards.suiteInterstitialCard.${translationKey}`
            })]
          }, index))
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [16, 23, 16],
            widths: [60, 70, 130],
            className: "flex-column",
            Content: CrmSuitePrice,
            contentProps: {
              productLevel,
              useDiscountedKey: SUITE_INTERSTITIAL
            }
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "p-top-3",
          children: !isCurrentPlanLevel && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [41],
            Content: SuiteTermSelectorContainer,
            contentProps: {
              productLevel,
              onChange: term => setQueryParamTerm(term),
              queryParamTerm,
              trackingSource: SUITE_CARD
            }
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "p-top-7",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [47],
            children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
              productLevel: productLevel,
              trackingSource: SUITE_CARD,
              ctaStyles: {
                width: '100%',
                lineHeight: '23px'
              },
              returnFirstCTAOnly: true
            })
          })
        })]
      })]
    }), isStarterGWP && /*#__PURE__*/_jsx(Small, {
      use: "help",
      className: "align-end p-x-6 p-bottom-8",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.promotions.starterGWPDisclaimer"
      })
    })]
  });
};

export default SuiteStarterCard;